import type { JSX } from 'react';
import type { WireArtifact } from '@logistics/contracts';
import { ago } from '@/lib/format';
import { now } from '@/lib/clock';
import { clockLeft } from './artifact-meta';
import { caption } from './parts';

type Hos = Extract<WireArtifact, { kind: 'hos' }>;
type Clock = Hos['clocks'][number];

/** A provider's percentage as a fraction: some send 0–1, some 0–100. */
function fraction(pct: number | undefined): number | undefined {
  if (pct === undefined) return undefined;
  return Math.max(0, Math.min(1, pct > 1 ? pct / 100 : pct));
}

/** The driver's clocks as rings, showing only the clocks the provider reported. */
export function HosArtifact({ artifact }: { artifact: Hos }): JSX.Element {
  return (
    <div className="flex flex-col gap-3.5">
      {artifact.clocks.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {artifact.clocks.map((clock, i) => (
            <ClockRing key={i} clock={clock} />
          ))}
        </div>
      )}
      <div className="border-line bg-surf text-mute rounded-[10px] border px-3.5 py-3 text-[13px] leading-normal">
        <span className="text-ink font-medium">Status: {artifact.status ?? 'not reported'}.</span>{' '}
        Reported {ago(artifact.at, now())}.
      </div>
    </div>
  );
}

function ClockRing({ clock }: { clock: Clock }): JSX.Element {
  const share = fraction(clock.pct);
  const color = share !== undefined && share < 0.15 ? 'var(--warn)' : 'var(--acc)';
  const fill =
    share === undefined
      ? 'var(--surf2)'
      : `conic-gradient(${color} ${share * 100}%, var(--surf2) 0)`;
  return (
    <div className="border-line bg-surf flex flex-col items-center gap-2 rounded-xl border p-4">
      <div className="grid size-24 place-items-center rounded-full" style={{ background: fill }}>
        <div className="bg-surf grid size-[74px] place-items-center rounded-full text-center">
          <span className="font-mono text-[15px] font-medium">
            {clockLeft(clock.remainingMinutes)}
          </span>
        </div>
      </div>
      <div className={`${caption} text-center`}>{clock.label.toUpperCase()}</div>
    </div>
  );
}
